import React, { useState, useEffect } from "react";
import { Tooltip } from "@themuse/design-system";
import { triggerSnowplowClickEvent, triggerSnowplowImpressionEvent } from "@themuse/snowplow-js-client";
import { useInView } from "react-intersection-observer";

import { useSearchHistory } from "hooks/SearchHistoryContext";
import { SearchHistoryEntry } from "interfaces/context/searchHistoryInterfaces";
import { useSearchState } from "hooks/SearchContext";

import styles from "./RecentSearches.module.scss";

/**
 * Returns a JSON string representing the click tracking data for a specific link within the RecentSearch Module.
 */
export const getClickTracking = (text: string, searchId: string) => ({
    sp_tile_type: null,
    sp_tile_entity_id: null,
    sp_click_component: "link",
    sp_click_link_text: text.length > 155 ? `${text.slice(0, 152)}...` : text,
    sp_click_position: "left_rail",
    sp_click_target_page_section: "search",
    sp_click_search_id: searchId
});

/**
 * Returns a JSON string representing the impression tracking data for the RecentSearch Module.
 */
export const getImpressionTracking = (searchId: string) => JSON.stringify({
    sp_impression_component: "insert",
    sp_impression_insert_type: "recent_searches",
    sp_impression_position: "left_rail",
    sp_impression_search_id: searchId,
});

/**
 * Triggers a click event for snowplow tracking purposes.
 */
export const triggerClickEvent = (event: React.SyntheticEvent<HTMLAnchorElement>, searchId: string) => {
    const text = event.currentTarget.innerText;
    const tracking = getClickTracking(text, searchId);
    triggerSnowplowClickEvent(tracking);
};

/**
 * React component for displaying recent searches with click tracking and impression events.
 * Utilizes search history and search state hooks to render recent search items with tooltips.
 */
export const RecentSearches = () => {
    const [open, setOpen] = useState<boolean>(false);
    const { searchHistory } = useSearchHistory();
    const { searchId } = useSearchState();
    const panelView = useInView({ triggerOnce: true, rootMargin: "0px" });
    const [recentSearchesViewed, setRecentSearchesViewed] = useState<boolean>(false);

    // Trigger snowplow impression once. If the pane is visible.
    useEffect(() => {
        if (panelView.inView && !recentSearchesViewed) {
            triggerSnowplowImpressionEvent(panelView.entry.target as HTMLElement);
            setRecentSearchesViewed(true);
        }
    }, [panelView.inView]);

    // Nothing in local storage, do not render.
    if (!searchHistory?.length) return null;

    return (
        <>
            <h2 className={styles.heading}>Recent Searches</h2>
            <div
                className={styles.RecentSearches}
                ref={panelView.ref}
                data-tracking-event-type="impression"
                data-tracking-props={getImpressionTracking(searchId)}
            >
                {searchHistory.map((searchItem: SearchHistoryEntry, index: number) => {
                    const { path, text, filters } = searchItem;
                    const content: string[] = text.split(" • ");
                    const linkStyle = index === 0 ? `${styles.link} ${styles.current}` : styles.link;
                    let tipStyle = null;
                    if (content.length > 20) tipStyle = styles.toolTipTwoColum;
                    if (content.length > 40) tipStyle = styles.toolTipThreeColum;

                    return open || index <= 1 ? (
                        <div key={path} className={`${styles.search} ${tipStyle}`}>
                            <a
                                className={linkStyle}
                                href={path}
                                onClick={(event) => triggerClickEvent(event, searchId)}
                            >
                                <div>
                                    {text}
                                </div>
                            </a>
                            <Tooltip {...{
                                title: path,
                                triggerText: filters.toString(),
                                content
                            }}
                            />
                        </div>
                    ) : null;
                })}

                {searchHistory.length > 2 ? (
                    <button
                        type="button"
                        onClick={() => setOpen(!open)}
                        className={styles.moreLess}
                    >
                        {open ? "VIEW LESS" : "VIEW MORE"}
                    </button>
                ) : null}
            </div>
        </>
    );
};

RecentSearches.displayName = "RecentSearches";
